import { Button, Card, CardBody, Divider } from '@heroui/react';
import React, { useState } from 'react';
import { Control, UseFormWatch } from 'react-hook-form';

import { LinkBuyerModal } from '../../BuyerLinking/LinkBuyerModal';
import { SelectedBuyersList } from '../../BuyerLinking/SelectedBuyersList';
import { BuyerSelectOption } from '../../BuyerLinking/types';
import { LinkSupplierModal } from '../../SupplierLinking/LinkSupplierModal';
import { SupplierSelectOption } from '../../SupplierLinking/utils';
import { UserFormData } from '../hooks';

interface CreateUserLinkingSectionProps {
  control: Control<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  onBuyerSelect: (buyers: BuyerSelectOption[]) => void;
  onSupplierSelect: (supplier: SupplierSelectOption | null) => void;
  selectedBuyers: BuyerSelectOption[];
  selectedSupplier: SupplierSelectOption | null;
  disabled?: boolean;
}

export const CreateUserLinkingSection: React.FC<
  CreateUserLinkingSectionProps
> = ({
  control,
  watch,
  onBuyerSelect,
  onSupplierSelect,
  selectedBuyers,
  selectedSupplier,
  disabled = false,
}) => {
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isBuyerModalOpen, setIsBuyerModalOpen] = useState(false);

  const handleBuyerSuccess = (newBuyers: BuyerSelectOption[]) => {
    // Add only new buyers that aren't already selected
    const uniqueBuyers = newBuyers.filter(
      (newBuyer) => !selectedBuyers.some((b) => b.value === newBuyer.value)
    );
    onBuyerSelect([...selectedBuyers, ...uniqueBuyers]);
    setIsBuyerModalOpen(false);
  };

  const handleBuyerRemove = (buyerToRemove: BuyerSelectOption) => {
    const updatedBuyers = selectedBuyers.filter(
      (buyer) => buyer.value !== buyerToRemove.value
    );
    onBuyerSelect(updatedBuyers);
  };

  const handleSupplierSuccess = (supplier: SupplierSelectOption) => {
    onSupplierSelect(supplier);
    setIsSupplierModalOpen(false);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-foreground">
                Link to Buyers
              </h3>
              {selectedBuyers.length > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
                  {selectedBuyers.length}
                </span>
              )}
            </div>
            <p className="text-sm text-default-500 mt-1">
              Link this user to buyers to give them access to buyer-specific
              features and chats.
            </p>
          </div>
          <Button
            color="primary"
            variant="flat"
            size="sm"
            onPress={() => setIsBuyerModalOpen(true)}
            isDisabled={disabled}
          >
            + Link Buyers
          </Button>
        </div>

        {selectedBuyers.length > 0 ? (
          <SelectedBuyersList
            buyers={selectedBuyers}
            onDeleteBuyer={handleBuyerRemove}
            onBuyerUserRoleChange={() => {}} // Not needed in create mode
            userName=""
            userId=""
            disabled={disabled}
          />
        ) : (
          <div className="text-center py-6 bg-default-50 rounded-lg border-2 border-dashed border-default-200">
            <p className="text-default-500 mb-2">No buyers selected yet.</p>
            <p className="text-sm text-default-400">
              Click "Link Buyers" to select buyers for this user.
            </p>
          </div>
        )}
      </div>

      <Divider />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-foreground">
                Link to Distributor
              </h3>
              {selectedSupplier && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-success-700 bg-success-100 rounded-full">
                  1
                </span>
              )}
            </div>
            <p className="text-sm text-default-500 mt-1">
              Link this user to a distributor to give them distributor-specific
              access.
            </p>
          </div>
          {!selectedSupplier && (
            <Button
              color="primary"
              variant="flat"
              size="sm"
              onPress={() => setIsSupplierModalOpen(true)}
              isDisabled={disabled}
            >
              + Link Distributor
            </Button>
          )}
        </div>

        {selectedSupplier ? (
          <Card className="shadow-sm">
            <CardBody className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-default-900 truncate">
                    {selectedSupplier.label}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    color="primary"
                    variant="light"
                    size="sm"
                    onPress={() => setIsSupplierModalOpen(true)}
                    isDisabled={disabled}
                  >
                    Change
                  </Button>
                  <Button
                    color="danger"
                    variant="light"
                    size="sm"
                    onPress={() => onSupplierSelect(null)}
                    isDisabled={disabled}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : (
          <div className="text-center py-6 bg-default-50 rounded-lg border-2 border-dashed border-default-200">
            <p className="text-default-500 mb-2">
              No distributor selected yet.
            </p>
            <p className="text-sm text-default-400">
              Click "Link Distributor" to select a distributor for this user.
            </p>
          </div>
        )}
      </div>

      <LinkBuyerModal
        isOpen={isBuyerModalOpen}
        onClose={() => setIsBuyerModalOpen(false)}
        onSuccess={(buyer) => handleBuyerSuccess([buyer])}
        userId=""
        userName=""
        linkedBuyers={selectedBuyers}
      />

      <LinkSupplierModal
        isOpen={isSupplierModalOpen}
        onClose={() => setIsSupplierModalOpen(false)}
        onSuccess={handleSupplierSuccess}
        userId=""
        userName=""
      />
    </>
  );
};
